import React, { useState, useEffect, useRef } from 'react';
import { getSupabaseClient } from '../../services/supabase';
import Portal from '../Portal';

export interface OtpModalProps {
  email: string;
  onClose: () => void;
  onEditEmail: () => void;
  onVerified: () => void;
}

export default function OtpModal({ email, onClose, onEditEmail, onVerified }: OtpModalProps) {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Only accept 6 digits
    if (!/^\d{6}$/.test(pastedData)) {
      return;
    }

    const digits = pastedData.split('');
    const newCode = [...code];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });
    setCode(newCode);
    setError(null);

    // Focus last input
    inputRefs.current[5]?.focus();
  };

  const handleVerify = async () => {
    const otpCode = code.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email',
      });

      if (error) {
        setError(error.message || 'Invalid code. Please try again.');
        setLoading(false);
        return;
      }

      // Success - auth state will update via onAuthStateChange
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();
      const redirectTo = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        setError(error.message || 'Failed to resend code. Please try again.');
      } else {
        // Reset countdown
        setResendCooldown(30);
        // Clear code inputs
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  const isCodeComplete = code.every((digit) => digit !== '') && code.join('').length === 6;

  return (
    <Portal>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="bg-bg-panel border border-border rounded-modal p-4 sm:p-6 md:p-8 w-full max-w-[min(450px,100%)] my-auto">
        <h2 className="text-h1 font-medium text-white mb-1 sm:mb-2">Check your email</h2>
        <p className="text-body text-text-muted mb-4 sm:mb-6">to continue to GenAnything</p>

        {/* Email display with edit button */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-center gap-2">
            <span className="text-white text-xs sm:text-sm break-all">{email}</span>
            <button
              onClick={onEditEmail}
              className="text-white/60 hover:text-white transition-colors flex-shrink-0"
              type="button"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* OTP Input Boxes */}
        <div className="flex gap-1.5 sm:gap-2 justify-center mb-4 sm:mb-6 px-2">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-medium bg-white text-black rounded-button border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white"
              disabled={loading}
            />
          ))}
        </div>

        {/* Resend Link */}
        <div className="text-center mb-4 sm:mb-6">
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || isResending || loading}
            className="text-text-subtle hover:text-white text-body-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Didn&apos;t receive a code? Resend {resendCooldown > 0 ? `(${resendCooldown})` : ''}
          </button>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleVerify}
          disabled={!isCodeComplete || loading}
          className="w-full bg-[#F9F7EA] text-[#0f1218] px-4 sm:px-6 py-3 min-h-[44px] rounded-button text-body font-medium hover:bg-[#E7D7B1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-3 sm:mb-4"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </>
          )}
        </button>

        {/* Error Message */}
        {error && (
          <div className="text-red-400 text-body-sm text-center mb-3 sm:mb-4">
            {error}
          </div>
        )}

        {/* Cancel Button */}
        <button
          onClick={onClose}
          className="w-full text-text-subtle hover:text-white transition-colors text-body-sm"
        >
          Cancel
        </button>
      </div>
      </div>
    </Portal>
  );
}

